import { Button, ContentCard, ListCard } from '@dfh-corp-sites/ui'
import { Play } from '@dfh-corp-sites/ui/icons'
import { ChevronRight } from '@material-ui/icons'
import { SanityImageSource } from '@sanity/asset-utils'
import React, { useMemo } from 'react'

import {
  PharmacyResourceType,
  ProfessionalResourceType,
  PublicResourceType,
} from '../../../graphql/api'
import useMediaQuery from '../../../hooks/useMediaQuery'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { getResourceAssetUrl, hasResourceAssetPage } from '../../../lib/library'
import useSanityImage from '../../../lib/sanity/hooks/useSanityImage'
import { libraryResourceIcon, libraryResourceName } from '../../../utils/libraryTools'
import Link from '../../global/Link'

type ResourceLinkProps = {
  view?: 'grid' | 'list'
  orientation?: 'horizontal' | 'vertical'
  imageFit?: 'contain' | 'cover'
  className?: string
  resourceType: PharmacyResourceType | ProfessionalResourceType | PublicResourceType
  category?: string
  title: string
  thumbnailImage?: {
    json?: string | null
  } | null
  asset?: SanityImageSource
  slug: string
  shortDescription?: string | null
  publishedDate?: string
  onProtocolClick?: (slug: string) => void
}

const ResourceLink: React.FC<ResourceLinkProps> = ({
  view,
  orientation,
  className,
  slug,
  resourceType,
  category,
  title,
  thumbnailImage,
  asset,
  onProtocolClick,
  shortDescription,
  publishedDate,
}) => {
  const isMobile = useMediaQuery({ maxWidth: '768px' })
  const isTablet = useMediaQuery({ minWidth: '769px', maxWidth: '1024px' })

  const imageSource = useMemo(() => {
    if (thumbnailImage?.json) {
      return { sourceJson: thumbnailImage.json }
    }

    return { source: asset }
  }, [thumbnailImage, asset])

  const sanityImageUrl = useSanityImage(imageSource, {
    dpr: 2,
    fit: 'max',
    // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
    width: view === 'list' ? 100 : 355,
  })

  const device = useMemo(() => {
    if (isMobile) return 'mobile'
    if (isTablet) return 'tablet'
    return 'desktop'
  }, [isMobile, isTablet])
  const image = sanityImageUrl || libraryResourceIcon({ resourceType, device })
  const alt = title || libraryResourceName({ resourceType })
  const target = hasResourceAssetPage({ _type: resourceType }) ? '_self' : '_blank'

  const handleClick = (e: React.MouseEvent) => {
    // onProtocolClick should open the protocol modal, if it exists
    // we prevent the navigation
    if (resourceType === 'protocol' && onProtocolClick) {
      e.preventDefault()
      onProtocolClick?.(slug)
    }

    setAnalyticsEvent({
      event: 'library_view_asset',
      title: title,
      subtitle: category || '',
    })
  }
  const cta = (
    <Button asChild variant="text">
      <Link
        target={target}
        className="!text-brand-navy"
        to={getResourceAssetUrl({
          _type: resourceType,
          slug: {
            current: slug,
          },
        })}
        onClick={handleClick}
      >
        {resourceType === 'webinar' ? (
          <span className="flex gap-2.5 items-center">
            <Play /> Watch Video
          </span>
        ) : (
          <span>
            Read Article <ChevronRight />
          </span>
        )}
      </Link>
    </Button>
  )

  if (view === 'list') {
    return (
      <ListCard
        title={title}
        heading={category || ''}
        cta={cta}
        image={{
          alt: alt || '',
          src: image || '',
          loading: 'eager' as const,
        }}
      />
    )
  }

  return (
    <ContentCard
      type={category}
      className={className}
      title={title}
      image={{
        alt: alt || '',
        src: image || '',
        loading: 'eager' as const,
      }}
      cta={cta}
      description={shortDescription}
      orientation={orientation}
      publishedDate={publishedDate}
      imageFit="cover"
    />
  )
}

export default ResourceLink
